import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { fetchStoriesCatalog, resolveStoryAssetUrl } from '../../utils/storiesFetcher';
import { getCharacter } from '../../utils/characters';
import { trackCharacterSelected } from '../../utils/analytics';

const SESSION_KEY = 'explore_stories_character';

/** Canonical display order for the three main characters. */
const CHARACTER_ORDER = ['Pebbles', 'Milo', 'Vector'];

// ---------------------------------------------------------------------------
// Styles (match TopicsPage visual language)
// ---------------------------------------------------------------------------

const pageStyle = {
  minHeight: '100vh',
  background: 'var(--color-bg)',
  padding: '28px 18px 48px',
  maxWidth: 760,
  margin: '0 auto',
  boxSizing: 'border-box',
};

const backLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--color-primary)',
  textDecoration: 'none',
  marginBottom: 24,
};

const introStyle = {
  marginBottom: 28,
};

const titleStyle = {
  fontSize: 'clamp(1.5rem, 4vw, 1.85rem)',
  fontWeight: 800,
  color: 'var(--color-text)',
  margin: '0 0 8px',
  letterSpacing: '-0.02em',
};

const subtitleStyle = {
  fontSize: 16,
  lineHeight: 1.5,
  color: 'var(--color-text)',
  opacity: 0.65,
  margin: 0,
};

// 1 col on phones, 2 cols at ~440px, 3 cols at ~660px — matches TopicsPage auto-fill pattern
const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))',
  gap: 18,
};

// ---------------------------------------------------------------------------
// SkeletonCard — three pulsing placeholders while catalog is in flight
// ---------------------------------------------------------------------------

function SkeletonCard() {
  return (
    <div
      style={{
        borderRadius: 18,
        border: '1px solid var(--color-border)',
        overflow: 'hidden',
        boxShadow: '0 4px 18px var(--color-shadow)',
      }}
      aria-hidden
    >
      <div
        className="stories-skeleton"
        style={{ height: 200, background: 'var(--color-border)' }}
      />
      <div style={{ padding: '16px 18px 20px' }}>
        <div
          className="stories-skeleton"
          style={{
            height: 20,
            borderRadius: 8,
            width: '55%',
            marginBottom: 10,
            background: 'var(--color-border)',
          }}
        />
        <div
          className="stories-skeleton"
          style={{
            height: 13,
            borderRadius: 6,
            width: '88%',
            marginBottom: 6,
            background: 'var(--color-border)',
          }}
        />
        <div
          className="stories-skeleton"
          style={{
            height: 13,
            borderRadius: 6,
            width: '68%',
            background: 'var(--color-border)',
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CharacterCard
// ---------------------------------------------------------------------------

function CharacterCard({ character, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'block',
        width: '100%',
        padding: 0,
        borderRadius: 18,
        border: '1px solid var(--color-border)',
        overflow: 'hidden',
        background: 'var(--color-surface, var(--color-bg-card, var(--color-bg)))',
        boxShadow: hovered
          ? '0 12px 32px var(--color-shadow)'
          : '0 4px 18px var(--color-shadow)',
        transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
        cursor: 'pointer',
        textAlign: 'left',
      }}
      aria-label={`${character.title} — tap to see stories`}
    >
      {/* Character image */}
      {character.teaser ? (
        <img
          src={resolveStoryAssetUrl(character.teaser)}
          alt=""
          aria-hidden
          style={{
            width: '100%',
            height: 200,
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        <div
          aria-hidden
          style={{
            width: '100%',
            height: 200,
            background: 'var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 52,
          }}
        >
          📖
        </div>
      )}

      {/* Card body */}
      <div style={{ padding: '16px 18px 20px' }}>
        <div
          style={{
            fontSize: 'clamp(1.05rem, 3vw, 1.2rem)',
            fontWeight: 800,
            color: 'var(--color-text)',
            marginBottom: 6,
            letterSpacing: '-0.01em',
            lineHeight: 1.2,
          }}
        >
          {character.title}
        </div>
        {character.desc ? (
          <div
            style={{
              fontSize: 14,
              lineHeight: 1.5,
              color: 'var(--color-text)',
              opacity: 0.65,
            }}
          >
            {character.desc}
          </div>
        ) : null}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// StoriesHomePage
// ---------------------------------------------------------------------------

export default function StoriesHomePage() {
  const navigate = useNavigate();
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const catalog = await fetchStoriesCatalog();

      // Keep only catalog entries whose title matches a known protagonist.
      // getCharacter() is case-insensitive and returns null for unknowns.
      const known = catalog.filter((entry) => getCharacter(entry.title) !== null);

      // Impose canonical display order; drop any character not in the list.
      const ordered = CHARACTER_ORDER
        .map((name) =>
          known.find((c) => c.title.toLowerCase() === name.toLowerCase()),
        )
        .filter(Boolean);

      setCharacters(ordered);
    } catch (err) {
      console.error('[StoriesHomePage] catalog fetch failed:', err);
      setError('Could not load stories. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const handleSelect = useCallback(
    (character) => {
      trackCharacterSelected({ character: character.title });
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(character));
      } catch {
        // sessionStorage may be unavailable in some sandboxed/embedded contexts
      }
      navigate(`/stories/${character.title.toLowerCase()}`);
    },
    [navigate],
  );

  return (
    <>
      <style>{`
        @keyframes stories-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.38; }
        }
        .stories-skeleton {
          animation: stories-pulse 1.4s ease-in-out infinite;
        }
      `}</style>

      <div style={pageStyle}>
        {/* Back to home — sidebar is hidden on /stories routes */}
        <Link to="/" style={backLinkStyle} aria-label="Back to home">
          <ChevronLeft size={20} strokeWidth={2.4} aria-hidden />
          Home
        </Link>

        {/* Page header */}
        <div style={introStyle}>
          <h1 style={titleStyle}>Stories</h1>
          <p style={subtitleStyle}>Choose a character</p>
        </div>

        {/* Loading — three skeleton cards */}
        {loading && (
          <div style={gridStyle} aria-busy="true" aria-label="Loading characters">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div
            role="alert"
            style={{
              textAlign: 'center',
              padding: '48px 24px',
            }}
          >
            <p
              style={{
                fontSize: 16,
                color: 'var(--color-text)',
                opacity: 0.75,
                marginBottom: 20,
                lineHeight: 1.5,
              }}
            >
              {error}
            </p>
            <button
              type="button"
              onClick={loadCatalog}
              style={{
                padding: '12px 24px',
                borderRadius: 12,
                border: 'none',
                background: 'var(--color-primary)',
                color: '#fff',
                fontSize: 15,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 2px 10px var(--color-shadow)',
              }}
            >
              Try again
            </button>
          </div>
        )}

        {/* Character grid */}
        {!loading && !error && (
          <div style={gridStyle}>
            {characters.map((character) => (
              <CharacterCard
                key={character.title}
                character={character}
                onClick={() => handleSelect(character)}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
